import { CornerDownRight, MessageSquare } from 'lucide-react'
import type { Comment } from '../../services/socialApi'

type ThreadComment = Comment & { role?: 'user' | 'agent' | 'expert'; replies?: ThreadComment[] }

interface Props {
  comment: ThreadComment
  depth?: number
  onReply?: (commentId: number, content: string) => void
}

export default function CommentCard({ comment, depth = 0, onReply }: Props) {
  const role = comment.role ?? resolveRole(comment)
  const authorName =
    comment.user?.display_name ||
    comment.user?.username ||
    comment.agent?.name ||
    (comment as any).agent_name ||
    (comment as any).user_display_name ||
    (comment as any).user_username ||
    (comment.user_id ? `User ${comment.user_id}` : null) ||
    (comment.agent_id ? `Agent ${comment.agent_id}` : null) ||
    'Anonymous'
  const initials = authorName.slice(0, 1).toUpperCase()
  const avatarBg = role === 'expert'
    ? 'linear-gradient(135deg, #7c3aed, #a855f7)'
    : role === 'agent'
      ? 'linear-gradient(135deg, #2563eb, #3b82f6)'
      : 'linear-gradient(135deg, #10b981, #059669)'

  return (
    <div style={{ marginLeft: depth > 0 ? 18 : 0, paddingLeft: depth > 0 ? 14 : 0, borderLeft: depth > 0 ? '1px solid rgba(148, 163, 184, 0.2)' : 'none' }}>
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '14px',
        borderRadius: '14px',
        background: depth > 0 ? 'rgba(248, 250, 252, 0.85)' : 'var(--card-bg, #fff)',
        border: '1px solid var(--border-color, #e2e8f0)',
      }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          flexShrink: 0,
          background: comment.user?.avatar_url || comment.agent?.avatar_url ? `url(${comment.user?.avatar_url || comment.agent?.avatar_url}) center/cover` : avatarBg,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: 14,
        }}>
          {!comment.user?.avatar_url && !comment.agent?.avatar_url && initials}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>{authorName}</div>
            <RoleBadge role={role} />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{formatDate(comment.created_at)}</div>
          </div>

          <div style={{ fontSize: '14px', lineHeight: 1.65, color: 'var(--text-secondary, #334155)', whiteSpace: 'pre-wrap' }}>
            {comment.content}
          </div>

          {onReply && (
            <button
              type="button"
              onClick={() => {
                const reply = window.prompt('Reply to this comment')
                if (reply?.trim()) {
                  onReply(comment.id, reply.trim())
                }
              }}
              style={{
                marginTop: '10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '10px',
                border: '1px solid rgba(79, 70, 229, 0.15)',
                background: 'rgba(79, 70, 229, 0.06)',
                color: '#4338ca',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <MessageSquare size={13} />
              Reply
            </button>
          )}
        </div>
      </div>

      {Array.isArray(comment.replies) && comment.replies.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
          {comment.replies.map((reply) => (
            <div key={reply.id} style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: -2, top: 14, color: 'var(--text-muted)' }}>
                <CornerDownRight size={14} />
              </div>
              <CommentCard comment={reply as ThreadComment} depth={depth + 1} onReply={onReply} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function resolveRole(comment: Comment): 'user' | 'agent' | 'expert' {
  if (comment.agent_id) {
    if (comment.agent?.agent_type === 'expert' || (comment as any).agent_type === 'expert') {
      return 'expert'
    }
    return 'agent'
  }

  return 'user'
}

function RoleBadge({ role }: { role: 'user' | 'agent' | 'expert' }) {
  const styles: Record<typeof role, { bg: string; color: string; label: string }> = {
    user: { bg: 'rgba(16, 185, 129, 0.12)', color: '#059669', label: 'user' },
    agent: { bg: 'rgba(37, 99, 235, 0.12)', color: '#2563eb', label: 'agent' },
    expert: { bg: 'rgba(124, 58, 237, 0.12)', color: '#7c3aed', label: 'expert' },
  }
  const style = styles[role]

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '999px',
      background: style.bg,
      color: style.color,
      fontSize: '11px',
      fontWeight: 800,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    }}>
      {style.label}
    </span>
  )
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}
